/**
 * @fileoverview Workaround para reproducción de audio WhatsApp en Firefox.
 * Recarga el audio en un iframe si falla la reproducción nativa, permitiendo reproducir archivos de audio en WhatsApp Web.
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades, enfocado únicamente en la manipulación de elementos DOM para solucionar bugs de audio en Firefox.
 * Incluye optimizaciones de performance y documentación completa de flujos y tipos de datos.
 * @module firefoxAudioWorkaround
 * @category Hook - UI Layer
 * @since 3.0.0
 * @version 3.1.0 - Optimización de performance, documentación JSDoc completa, workaround para Firefox audio WhatsApp
 */

/**
 * @typedef {Object} FirefoxAudioEvent
 * @description Evento de reproducción de audio en WhatsApp Web.
 * @property {EventTarget} target - Elemento de audio que disparó el evento.
 */

/**
 * Workaround para reproducción de audio WhatsApp en Firefox.
 *
 * **Flujo de operación:**
 * 1. Crea un iframe y lo carga en el DOM.
 * 2. Al cargar el iframe, inserta un nuevo elemento audio con la fuente del audio original.
 * 3. Pausa el audio original y lo reemplaza por el nuevo audio en el iframe.
 * 4. Si el audio ya existe, lo recarga usando fetch y reemplaza el elemento por el iframe.
 *
 * @function firefoxAudioWorkaround
 * @param {FirefoxAudioEvent} e - Evento de reproducción de audio en WhatsApp Web.
 * @returns {void}
 * @example
 * audioElement.addEventListener('play', firefoxAudioWorkaround);
 * @since 3.0.0
 * @version 3.1.0 - Workaround para Firefox audio WhatsApp
 */

export default function firefoxAudioWorkaround(e) {
  let aud;
  const frame = document.createElement('iframe');
  frame.onload = () => {
    const doc = frame.contentDocument;
    const inner_aud = e.target;
    const new_aud = doc.createElement('audio');
    new_aud.src = inner_aud.currentSrc;
    inner_aud.pause();
    new_aud.controls = true;
    frame.replaceWith(new_aud);
    aud = new_aud;
    aud.play();
  };
  if (aud) {
    fetch(aud.src)
      .then((r) => r.blob())
      .then((b) => {
        frame.src = URL.createObjectURL(b);
        aud.replaceWith(frame);
      });
  }
}
